import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TableCompo from "../CommonTable/TableCompo";
import { getCustomerOfflineTransactionsRequest } from "../../redux/OfflineOrder/offlineOrderSlice";
import useCurrency from "../../hooks/useCurrency";
import { useAppTheme } from "../../theme/useAppTheme";

const getProofUrl = (path) => {
  if (!path || typeof path !== "string") return "";
  return path.startsWith("http")
    ? path
    : `${process.env.REACT_APP_CDN_URL}/${process.env.REACT_APP_PACKAGE_NAME}/${path}`;
};

const OfflineTransactionScreen = () => {
  const dispatch = useDispatch();

  const {
    transactions: offlineTransactions,
    transactionsLoading: loading,
    transactionsError: error,
  } = useSelector((state) => state.offline_order || {});
  const { appConfig } = useSelector((state) => state.dashboard || {});
  const decimal = appConfig?.decimalPrecision ?? 4;

  const [filterType, setFilterType] = useState("All");
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [selectedTxn, setSelectedTxn] = useState(null);

  const { symbol } = useCurrency();
  const themeColors = useAppTheme();

  useEffect(() => {
    dispatch(getCustomerOfflineTransactionsRequest());
  }, [dispatch]);

  const filteredOfflineTransactions = useMemo(() => {
    if (!Array.isArray(offlineTransactions)) return [];
    if (filterType === "All") return offlineTransactions;
    return offlineTransactions.filter((item) =>
      item.order?.order_type?.toLowerCase().includes(filterType.toLowerCase()),
    );
  }, [offlineTransactions, filterType]);

  const columns = [
    { field: "date", headerName: "Date & Time", flex: 1.2 },
    { field: "metalName", headerName: "Metal Name", flex: 1 },
    { field: "transactionType", headerName: "Transaction Type", flex: 1.2 },
    { field: "orderStatus", headerName: "Order Status", flex: 1 },
    { field: "amount", headerName: "Amount", flex: 1 },
    { field: "weight", headerName: "Weight (gms)", flex: 1 },
    {
      field: "paymentDetails",
      headerName: "Payment Details",
      flex: 1.5,
      render: (_value, row) => (
        <Button
          size="small"
          variant="outlined"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedTxn(row?.original || null);
          }}
          sx={{
            borderRadius: "20px",
            textTransform: "none",
            color: themeColors.primary,
            borderColor: themeColors.primary,
          }}
        >
          View
        </Button>
      ),
    },
  ];

  const tableData = filteredOfflineTransactions.map((item, index) => ({
    id: item?._id || index + 1,
    date: item?.createdAt
      ? new Date(item.createdAt).toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "N/A",
    metalName: item?.order?.metalName || "N/A",
    transactionType: item?.order?.order_type || "N/A",
    orderStatus: item?.order?.status || item?.status || "N/A",
    amount: `${symbol}${Number(item?.order?.amount ?? 0).toFixed(decimal)}`,
    weight:
      item?.order?.weight != null
        ? Number(item.order.weight).toFixed(decimal)
        : "N/A",
    paymentDetails: "View",
    original: item,
  }));

  const handleTableChange = (type, payload) => {
    if (type === "changePage") {
      setPage(payload.page);
    }
    if (type === "changeRowsPerPage") {
      setLimit(payload.rowsPerPage);
      setPage(0);
    }
  };

  const paginatedData = tableData.slice(page * limit, page * limit + limit);

  const proofUrl = getProofUrl(selectedTxn?.document);

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 1300,
        mx: "auto",
        mt: 8,
        px: { xs: 1.5, sm: 2 },
        color: themeColors.scaffoldText,
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: themeColors.primary,
            fontFamily: "Aeonik",
            mb: 1.5,
          }}
        >
          Offline Transactions
        </Typography>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1, mb: 2 }}>
          <FormControl
            size="small"
            sx={{
              minWidth: 200,
              "& .MuiSelect-select": { color: themeColors.primary },
            }}
          >
            <InputLabel>Filter</InputLabel>
            <Select
              value={filterType}
              onChange={(e) => {
                setFilterType(e.target.value);
                setPage(0);
              }}
              label="Filter"
              MenuProps={{
                PaperProps: {
                  sx: {
                    background: themeColors.mediumContainer,
                    color: themeColors.text,
                  },
                },
              }}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="Buy">Buy</MenuItem>
              <MenuItem value="Sell">Sell</MenuItem>
              <MenuItem value="Gift">Gift</MenuItem>
              <MenuItem value="Redeem">Redeem</MenuItem>
              <MenuItem value="Sip">SIP</MenuItem>
              <MenuItem value="Installment">Installment</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ textAlign: "center", mt: 10 }}>
          <CircularProgress color="primary" />
          <Typography variant="body2" mt={2}>
            Loading offline transactions...
          </Typography>
        </Box>
      ) : error ? (
        <Typography color="error" textAlign="center" mt={5}>
          {error}
        </Typography>
      ) : (
        <Box
          sx={{
            height: "70vh",
            width: "100%",
            minWidth: 0,
            overflow: "auto",
          }}
        >
          <TableCompo
            title="Offline Transactions"
            columns={columns}
            data={paginatedData}
            page={page}
            limit={limit}
            totalCount={tableData.length}
            onTableChange={handleTableChange}
            loading={loading}
          />
        </Box>
      )}

      <Dialog
        open={Boolean(selectedTxn)}
        onClose={() => setSelectedTxn(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: themeColors.bigContainer,
            border: `1px solid ${themeColors.scaffold}`,
            color: themeColors.text,
          },
        }}
      >
        <IconButton
          onClick={() => setSelectedTxn(null)}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: themeColors.text,
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent sx={{ pt: 5 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, mb: 2, color: themeColors.primary }}
          >
            Payment Details
          </Typography>

          {[
            ["Order ID", selectedTxn?.order?.order_id],
            [
              "Transaction ID",
              selectedTxn?.transactionId ?? 'N/A',
            ],
          ].map(([label, value]) => (
            <Box
              key={label}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                mb: 1.5,
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>{label} :</Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  wordBreak: "break-all",
                  textAlign: "right",
                }}
              >
                {value || "N/A"}
              </Typography>
            </Box>
          ))}

          <Divider sx={{ my: 2, opacity: 0.4 }} />

          <Typography sx={{ fontWeight: 600, mb: 1 }}>
            Transaction Proof :
          </Typography>

          {proofUrl ? (
            <Box
              component="a"
              href={proofUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Box
                component="img"
                src={proofUrl}
                alt="Transaction proof"
                sx={{
                  width: "100%",
                  maxHeight: 360,
                  objectFit: "contain",
                  borderRadius: 2,
                  border: `1px solid ${themeColors.scaffold}`,
                  display: "block",
                }}
              />
            </Box>
          ) : (
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              No proof uploaded.
            </Typography>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default OfflineTransactionScreen;
